import { createRef, useState } from 'react';
import { Stack, Box, Container, Tabs, ClickAwayListener, Collapse, TabsProps } from '@mui/material';
import { colors } from 'styles/theme/colors';
import useMenuSlugParser from 'hooks/useMenuSlugParser';
import useOnPathChange from 'hooks/onPathChange';
import useLayout from '../../../../stores/useLayout';
import { LevelOneTab } from './components/LevelOneTab';
import { DesktopWordpressMenuLevelTwo } from './components/DesktopWordpressMenuLevelTwo';
import DesktopProductSearchField from './components/DesktopProductSearchField';

export function DesktopWordpressMenu() {
    const menu = useLayout((state) => state.menu);
    const [activeIndex, setActiveIndex] = useState<number | false>(false);
    const tabRefs = Array.from({ length: menu.length }, () => createRef<HTMLDivElement>());
    const closeMenu = () => setActiveIndex(false);
    useOnPathChange(closeMenu);

    const toggleActiveIndex = (index: number) => {
        setActiveIndex(index);
    };

    const toggleActiveIndexWithFocus = (index: number) => {
        tabRefs[index].current?.focus();
    };

    const onTabsKeyDown: TabsProps['onKeyDown'] = (event) => {
        if (event.key === 'Escape') {
            closeMenu();
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            if (activeIndex === false) {
                toggleActiveIndexWithFocus(0);
            } else if (event.key === 'ArrowRight') {
                if (activeIndex < menu.length - 1) {
                    toggleActiveIndexWithFocus(activeIndex + 1);
                } else {
                    toggleActiveIndexWithFocus(0);
                }
            } else if (event.key === 'ArrowLeft') {
                if (activeIndex > 0) {
                    toggleActiveIndexWithFocus(activeIndex - 1);
                } else {
                    toggleActiveIndexWithFocus(menu.length - 1);
                }
            }
        }
    };

    const { parseSlug } = useMenuSlugParser();

    return (
        <ClickAwayListener onClickAway={closeMenu}>
            <Stack
                onMouseLeave={closeMenu}
                sx={{ alignItems: 'center' }}
            >
                <Container
                    disableGutters
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'flex-end',
                        alignItems: 'end',
                        columnGap: 4,
                        // Push up search bar if the menu is too large
                        flexWrap: 'wrap-reverse',
                    }}
                    maxWidth="xl"
                    component="nav"
                >
                    <Tabs
                        value={activeIndex}
                        indicatorColor="secondary"
                        selectionFollowsFocus
                        onKeyDown={onTabsKeyDown}
                        sx={{
                            flexGrow: 1,
                            '& .MuiTabs-indicator': { height: '3px' },
                            '& .MuiTabs-flexContainer': { display: 'inline-flex', justifyContent: 'space-between', minWidth: '88%' },
                        }}
                    >
                        {menu?.map((listItem, listItemIndex) => {
                            const href = listItem.items.length === 0 ? parseSlug(listItem) : '';
                            const tabActive = activeIndex === listItemIndex;
                            const color = tabActive ? 'secondary' : 'black';

                            return (
                                <LevelOneTab
                                    {...listItem}
                                    index={listItemIndex}
                                    color={color}
                                    href={href}
                                    action={() => toggleActiveIndex(listItemIndex)}
                                    key={listItemIndex}
                                    ref={tabRefs[listItemIndex]}
                                />
                            );
                        })}
                    </Tabs>
                    <DesktopProductSearchField onFocus={closeMenu} onClick={closeMenu} />
                </Container>
                <Box sx={{
                    width: '100dvw',
                    maxWidth: '100%',
                    borderBottom: `1px solid ${colors.gray1}`,
                }}
                />
                {menu.map((listItem, listItemIndex) => (listItem.items.length > 0 && (
                    <Container
                        sx={{
                            display: 'flex',
                            width: '100%',
                        }}
                        maxWidth="xl"
                        component="nav"
                        key={listItemIndex}
                    >
                        <DesktopWordpressMenuLevelTwo
                            activeIndex={activeIndex}
                            listItemIndex={listItemIndex}
                            listItem={listItem}
                            closeMenu={closeMenu}
                        />
                    </Container>
                )))}
            </Stack>
        </ClickAwayListener>
    );
}
